import React, { useState, useEffect, useContext } from "react";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { OwcIcon, OwcHyperlink } from "@one/react";
import styles from "./TopBannerNotification.module.scss";
import { BANNER_COLORS, NOTIFICATIONS_BANNER } from "../../constants";
import { withApollo } from "react-apollo";
import { LIST_NOTIFICATION_BANNER_HOME } from "../../gql/landingapi/queries";
import { SiteContext } from "../siteChange/context";

const DEFAULT_COLOR_SCHEME = {
  Heading: "var(--one-color-cobas-blue-600)",
  TextToDisplay: "var(--one-color-legacy-blue-200)",
  Icon: "circle_info",
  IconType: "outlined"
};

const NavigationArrow = React.memo(({
  direction,
  isDisabled,
  onClick,
  className
}) => {
  const ArrowIcon = direction === 'prev' ? ArrowBackIosNewIcon : ArrowForwardIosIcon;

  const style = {
    display: 'flex',
    alignItems: 'center',
    margin: direction === 'prev' ? '0 8px 0 16px' : '0 16px 0 8px',
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    border: 'none',
    background: 'none',
  };

  return (
    <button
      className={className}
      style={style}
      onClick={isDisabled ? undefined : onClick}
      tabIndex={isDisabled ? -1 : 0}
      aria-label={`${direction === 'prev' ? 'Previous' : 'Next'} banner`}
      aria-disabled={isDisabled}
    >
      <ArrowIcon fontSize="small" />
    </button>
  );
});


const TopBannerNotification = ({ TopBannerDatas, client }) => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { selected: selectedSite } = useContext(SiteContext);

  useEffect(() => {
    try {
      if (TopBannerDatas !== undefined) {
        const loadBannerLists = async () => {
            const { data: { listNotificationActiveHomeBanners } } = await client.query({
              query: LIST_NOTIFICATION_BANNER_HOME,
              variables: {
                site: selectedSite?.siteName
              },
              fetchPolicy: "network-only"
            });
            const banners = listNotificationActiveHomeBanners || [];
            banners.sort((a, b) => b.color.localeCompare(a.color));
            setBanners(banners);
            
        };
        setCurrentIndex(0);
        loadBannerLists();
      }
    } catch (e) {
      console.info("Server Error: Unable to fetch notification banners");
      setCurrentIndex(0);
      setBanners([])
    }
  }, [TopBannerDatas, selectedSite, client]);

  const handleNext = () => {
    setCurrentIndex(prevIndex =>
      prevIndex < banners.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handlePrev = () => {
    setCurrentIndex(prevIndex =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex
    );
  };

  const currentBanner = banners[currentIndex];
  const totalBanners = banners.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === banners.length - 1;

  let colorScheme = DEFAULT_COLOR_SCHEME;
  if (currentBanner) {
    const colorKey = Object.keys(NOTIFICATIONS_BANNER).find(
      key => NOTIFICATIONS_BANNER[key] === currentBanner.color
    );
    colorScheme = BANNER_COLORS[colorKey] || DEFAULT_COLOR_SCHEME;
  }

  const containerStyle = {
    borderLeft: `2px solid ${colorScheme.Heading}`,
    borderRight: `2px solid ${colorScheme.Heading}`,
    backgroundColor: colorScheme.TextToDisplay,
    display: 'flex',
    alignItems: 'center',
    width: '60%',
    justifyContent: (totalBanners > 1) ? 'space-between' : 'center',
    margin: '0 1rem',
    paddingLeft: '0',
    height: '3rem',
  };


  const iconLinkStyle = {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer'
  };

  const iconStyle = {
    color: colorScheme.Heading,
    fontSize: 30,
    paddingRight: 15
  };

  const hyperlinkIconStyle = {
    color: colorScheme.Heading,
    fontSize: 30,
    paddingRight: 15,
    cursor: 'pointer'
  };

  if (totalBanners === 0) {
    return null;
  }

  return (
    <div
      style={containerStyle}
      role="banner"
      aria-live="polite"
      aria-label={`Banner ${currentIndex + 1} of ${totalBanners}`}
    >


      {totalBanners > 1 && (
        <NavigationArrow
          direction="prev"
          isDisabled={isFirst}
          onClick={handlePrev}
          className={styles.BannerArrow}
        />
      )}
      <div className={styles.BannerContent}>
        {currentBanner.infoURL ?
          <OwcHyperlink
            href={currentBanner.infoURL}
            target="_blank"
            className={styles.IconLink}
            style={iconLinkStyle}
            aria-label="Read more information"
          >
            <OwcIcon
              name={colorScheme.Icon}
              type={colorScheme.IconType}
              style={hyperlinkIconStyle}
            />
          </OwcHyperlink>
          :
          <OwcIcon
            name={colorScheme.Icon}
            type={colorScheme.IconType}
            style={iconStyle}
          />
        }


        <span className={styles.BannerHeading}>
          <strong>{currentBanner.heading}</strong>
        </span>

        <span className={styles.BannerText}>
          {currentBanner.displayText}
        </span>
        {currentBanner.infoURL && (
          <OwcHyperlink
            className={styles.BannerLink}
            href={currentBanner?.infoURL}
            target="_blank"
            aria-label="Read more details"
          >
            <span style={{ fontSize: "12px" }}>Read More</span>
          </OwcHyperlink>
        )}
      </div>
      {totalBanners > 1 && (
        <NavigationArrow
          direction="next"
          isDisabled={isLast}
          onClick={handleNext}
          className={styles.BannerArrow}
        />
      )}
    </div>
  );
};

export default withApollo(TopBannerNotification);
